import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { passwordMeetsAllRules } from '@physioedge/shared';
import { api, ApiError, egp } from '../api';
import { useAuth } from '../auth';
import { useSupportPhone } from '../components/WhatsAppHelp';
import { Alert, Button, Card, Input, ModalOverlay, PageLoader, PasswordChecklist, PasswordInput } from '../components/ui';
import { clinicWelcomeIsPending, markClinicWelcomePending } from '../lib/clinicWelcome';
import { FormattedText } from '../lib/simpleMarkup';
import { formatTime12h } from '../lib/time12h';
import { formatEnNumber, westernDigits } from '../lib/westernDigits';

const TERMS_ACCEPT_WAIT_SECONDS = 30;

type AgreementPreview = {
  agreementVersion: string;
  agreementText: string;
  clinicName: string;
  payoutRatePerWeekEgp: number;
  internWeeks: number;
  totalRevenueEgp: number;
  scheduleStartDate: string | null;
  scheduleEndDate: string | null;
  durationWeeks: number;
  supportEmail: string;
  branches: Array<{
    id: string;
    name: string;
    city: string;
    capacityLabel: string;
    internWeeks: number;
    sessions: Array<{
      daysLabel: string;
      hoursLabel: string;
    }>;
  }>;
};

type AgreementSignature = {
  clinicNameTyped: string;
  representativeName: string;
  representativeTitle: string;
  acknowledgmentDate: string;
};

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function formatHoursLabel(raw: string): string {
  if (!raw.includes('–')) return raw;
  const [a, b] = raw.split('–');
  if (!a || !b) return raw;
  if (/^\d{1,2}:\d{2}$/.test(a.trim()) && /^\d{1,2}:\d{2}$/.test(b.trim())) {
    return `${formatTime12h(a.trim())} – ${formatTime12h(b.trim())}`;
  }
  return raw;
}

/**
 * Main clinic account first login: accept partner terms, then set a new
 * password, then open the dashboard.
 */
export function ClinicOnboardingPage() {
  const { me, loading: authLoading, refetch, logout } = useAuth();
  const navigate = useNavigate();
  const settingsQ = useSupportPhone();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [signature, setSignature] = useState<AgreementSignature | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const previewQ = useQuery({
    queryKey: ['clinic-agreement-preview'],
    queryFn: () => api.get<AgreementPreview>('/api/auth/clinic-agreement-preview'),
    enabled: Boolean(me?.role === 'clinic_staff' && me.needsPartnerTerms && me.isPrimaryClinicStaff),
  });

  if (authLoading) return <PageLoader variant="form" />;
  if (!me) return <Navigate to="/login" replace />;
  if (me.role !== 'clinic_staff') return <Navigate to="/" replace />;
  if (!me.needsClinicOnboarding && !me.mustChangePassword) {
    const pending = clinicWelcomeIsPending(me.id);
    return <Navigate to="/clinic" replace state={pending ? { clinicWelcome: true } : {}} />;
  }

  const mustChangePassword = Boolean(me.mustChangePassword);
  const needsPartnerTerms = Boolean(me.needsPartnerTerms);
  const isPrimary = Boolean(me.isPrimaryClinicStaff);
  const showPasswordAfterTerms = isPrimary && needsPartnerTerms;
  const showPasswordForm = mustChangePassword || (showPasswordAfterTerms && termsAccepted);
  const fallbackPolicy = me.partnerTermsText?.trim()
    || settingsQ.data?.clinicPartnershipPolicy
    || '';
  const agreementText = westernDigits((previewQ.data?.agreementText?.trim() || fallbackPolicy).trim());

  const passwordReady = !showPasswordForm || passwordMeetsAllRules(newPassword);
  const passwordsMatch = !showPasswordForm
    || newPassword.length === 0
    || confirmPassword.length === 0
    || newPassword === confirmPassword;
  const differentFromCurrent = !showPasswordForm
    || !currentPassword
    || newPassword !== currentPassword;

  const finishToDashboard = () => {
    markClinicWelcomePending(me.id);
    navigate('/clinic', { replace: true, state: { clinicWelcome: true } });
  };

  const completeOnboarding = async (opts: {
    currentPassword?: string;
    newPassword?: string;
    signature?: AgreementSignature;
  }) => {
    setError('');
    setLoading(true);
    try {
      const updated = await api.post<{
        needsClinicOnboarding?: boolean;
        needsPartnerTerms?: boolean;
        mustChangePassword?: boolean;
      }>('/api/auth/clinic-onboarding', {
        acceptTerms: true,
        ...(opts.signature ? {
          clinicNameTyped: opts.signature.clinicNameTyped,
          representativeName: opts.signature.representativeName,
          representativeTitle: opts.signature.representativeTitle,
          acknowledgmentDate: opts.signature.acknowledgmentDate,
        } : {}),
        ...(opts.currentPassword ? { currentPassword: opts.currentPassword } : {}),
        ...(opts.newPassword ? { newPassword: opts.newPassword } : {}),
      });
      if (!updated.needsClinicOnboarding && !updated.mustChangePassword) {
        markClinicWelcomePending(me.id);
      }
      await refetch();
      if (!updated.needsClinicOnboarding && !updated.mustChangePassword) {
        finishToDashboard();
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not complete onboarding.');
    } finally {
      setLoading(false);
    }
  };

  const acceptTerms = (sig: AgreementSignature) => {
    setSignature(sig);
    setTermsAccepted(true);
    setError('');
  };

  const submitPassword = async (e: FormEvent) => {
    e.preventDefault();
    if (needsPartnerTerms && isPrimary && (!termsAccepted || !signature)) {
      setError('Accept the PhysioEDGE partner terms to continue.');
      return;
    }
    if (!passwordReady) {
      setError('Password does not meet all requirements.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (currentPassword && newPassword === currentPassword) {
      setError('Choose a password that is different from your current one.');
      return;
    }
    await completeOnboarding({
      ...(currentPassword.trim() ? { currentPassword } : {}),
      newPassword,
      ...(signature ? { signature } : {}),
    });
  };

  const signOut = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const showTermsModal = needsPartnerTerms && isPrimary && !termsAccepted;

  return (
    <div
      className="min-h-dvh bg-bg flex items-end sm:items-center justify-center px-0 sm:px-4 pt-8 pb-0 sm:py-12"
      lang="en"
    >
      {showTermsModal ? (
        <PartnerTermsDialog
          agreementText={agreementText}
          preview={previewQ.data ?? null}
          previewLoading={previewQ.isLoading}
          defaultClinicName={previewQ.data?.clinicName ?? ''}
          defaultRepName={me.fullName}
          loading={loading}
          error={error}
          onAccept={acceptTerms}
          onSignOut={() => { void signOut(); }}
        />
      ) : (
        <Card className="w-full max-w-lg p-5 sm:p-8 rounded-t-2xl sm:rounded-2xl max-h-[92dvh] overflow-y-auto">
          <h1 className="font-heading text-xl sm:text-2xl font-bold text-ink mb-1">Welcome to PhysioEDGE</h1>
          <p className="text-sm text-ink-soft mb-5 sm:mb-6">
            {showPasswordForm
              ? (needsPartnerTerms && isPrimary
                ? 'Terms accepted. Choose a new password to open your dashboard.'
                : 'Choose a new password to open your dashboard.')
              : 'You can open your dashboard.'}
          </p>
          {error && <Alert type="error" className="mb-4">{error}</Alert>}
          {showPasswordForm ? (
            <form onSubmit={submitPassword} className="space-y-4 pb-[env(safe-area-inset-bottom)]">
              <PasswordInput
                label="Current password (if you have one)"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                error={currentPassword.length > 0 && newPassword.length > 0 && !differentFromCurrent
                  ? 'Choose a password that is different from your current one.'
                  : undefined}
              />
              <PasswordInput
                label="New password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
              <PasswordChecklist password={newPassword} currentPassword={currentPassword} />
              <PasswordInput
                label="Confirm new password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                error={confirmPassword.length > 0 && !passwordsMatch ? 'Passwords do not match' : undefined}
              />
              <Button
                type="submit"
                loading={loading}
                className="w-full"
                size="lg"
                disabled={!passwordReady || !passwordsMatch || !differentFromCurrent}
              >
                Set password and open dashboard
              </Button>
            </form>
          ) : (
            <Button
              type="button"
              loading={loading}
              className="w-full"
              size="lg"
              onClick={() => {
                void completeOnboarding({});
              }}
            >
              Open dashboard
            </Button>
          )}
        </Card>
      )}
    </div>
  );
}

function PartnerTermsDialog({
  agreementText,
  preview,
  previewLoading,
  defaultClinicName,
  defaultRepName,
  loading,
  error,
  onAccept,
  onSignOut,
}: {
  agreementText: string;
  preview: AgreementPreview | null;
  previewLoading: boolean;
  defaultClinicName: string;
  defaultRepName: string;
  loading: boolean;
  error: string;
  onAccept: (sig: AgreementSignature) => void;
  onSignOut: () => void;
}) {
  const [secondsLeft, setSecondsLeft] = useState(TERMS_ACCEPT_WAIT_SECONDS);
  const [clinicNameTyped, setClinicNameTyped] = useState(defaultClinicName);
  const [representativeName, setRepresentativeName] = useState(defaultRepName);
  const [representativeTitle, setRepresentativeTitle] = useState('');
  const [acknowledgmentDate, setAcknowledgmentDate] = useState(todayIsoDate());
  const [confirmed, setConfirmed] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (defaultClinicName && !clinicNameTyped) setClinicNameTyped(defaultClinicName);
  }, [defaultClinicName, clinicNameTyped]);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = window.setTimeout(() => setSecondsLeft(s => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [secondsLeft]);

  const canAccept = secondsLeft <= 0 && !loading;
  const supportEmail = preview?.supportEmail || 'support@physioedge.com';
  const clinicName = preview?.clinicName || defaultClinicName || 'Clinic';

  const submit = () => {
    setFormError('');
    if (!agreementText.trim()) {
      setFormError('Partner terms are not available yet. Contact PhysioEDGE support.');
      return;
    }
    if (clinicNameTyped.trim().length < 2) {
      setFormError('Enter the registered clinic name.');
      return;
    }
    if (representativeName.trim().length < 2) {
      setFormError('Enter your full name.');
      return;
    }
    if (representativeTitle.trim().length < 2) {
      setFormError('Enter your job title.');
      return;
    }
    if (!acknowledgmentDate) {
      setFormError('Enter the date.');
      return;
    }
    if (!confirmed) {
      setFormError('Tick the agreement box to continue.');
      return;
    }
    onAccept({
      clinicNameTyped: clinicNameTyped.trim(),
      representativeName: representativeName.trim(),
      representativeTitle: representativeTitle.trim(),
      acknowledgmentDate,
    });
  };

  const durationWeeks = preview?.durationWeeks ?? 0;
  const internWeeks = preview?.internWeeks ?? 0;
  const rate = preview?.payoutRatePerWeekEgp ?? 0;
  const totalRevenue = preview?.totalRevenueEgp ?? 0;
  const weekWord = (n: number) => (n === 1 ? 'week' : 'weeks');
  const monthWord = (n: number) => (n === 1 ? 'month' : 'months');

  /** Agreement length in months (intern capacity stays in weeks). */
  const durationMonths = (() => {
    const start = preview?.scheduleStartDate;
    const end = preview?.scheduleEndDate;
    if (!start || !end || start > end) {
      return durationWeeks > 0 ? Math.max(1, Math.round(durationWeeks / 4.345)) : 0;
    }
    const s = Date.parse(`${start}T12:00:00Z`);
    const e = Date.parse(`${end}T12:00:00Z`);
    if (!Number.isFinite(s) || !Number.isFinite(e)) return 0;
    const days = Math.round((e - s) / 86_400_000) + 1;
    return Math.max(1, Math.round(days / 30.4375));
  })();

  const stats = [
    {
      label: 'Duration',
      value: previewLoading
        ? '…'
        : durationMonths
          ? `${formatEnNumber(durationMonths)} ${monthWord(durationMonths)}`
          : '—',
      note: preview?.scheduleStartDate && preview?.scheduleEndDate
        ? `Agreement window: ${westernDigits(preview.scheduleStartDate)} → ${westernDigits(preview.scheduleEndDate)}`
        : 'First schedule day to last schedule day',
    },
    {
      label: 'Intern weeks',
      value: previewLoading
        ? '…'
        : `${formatEnNumber(internWeeks)} ${weekWord(internWeeks)}`,
      note: 'Bookable weeks × seats across every branch and session',
    },
    {
      label: 'Rate',
      value: previewLoading
        ? '…'
        : `${westernDigits(egp(rate))} / week`,
      note: 'Payout for each filled intern week',
    },
    {
      label: 'Full capacity',
      value: previewLoading
        ? '…'
        : durationMonths
          ? `${westernDigits(egp(totalRevenue))} / ${formatEnNumber(durationMonths)} ${monthWord(durationMonths)}`
          : westernDigits(egp(totalRevenue)),
      note: `Max total if all ${formatEnNumber(internWeeks)} intern ${weekWord(internWeeks)} fill`,
    },
  ];

  return (
    <ModalOverlay>
      <div
        role="dialog"
        lang="en"
        aria-modal="true"
        aria-labelledby="partner-terms-title"
        className="app-dialog bg-surface shadow-pop w-full max-w-3xl"
      >
        <header className="px-4 sm:px-8 pt-4 sm:pt-7 pb-3 sm:pb-4 border-b border-line shrink-0">
          <p className="text-xs text-ink-faint mb-1">{clinicName}</p>
          <h1 id="partner-terms-title" className="font-heading text-xl sm:text-2xl font-bold text-ink tracking-tight">
            Partner terms
          </h1>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 sm:px-8 py-4 sm:py-5 space-y-5 sm:space-y-6">
          <section>
            {previewLoading && !agreementText ? (
              <p className="text-sm text-ink-faint">Loading…</p>
            ) : agreementText ? (
              <FormattedText text={agreementText} className="text-sm text-ink-soft" />
            ) : (
              <p className="text-sm text-ink-faint">
                No terms set yet. Contact <span className="font-medium text-ink">{supportEmail}</span>.
              </p>
            )}
          </section>

          <section className="space-y-3">
            <h2 className="text-xs font-semibold uppercase tracking-[0.08em] text-ink-faint">Summary</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {stats.map(stat => (
                <div key={stat.label} className="rounded-xl border border-line bg-bg px-3.5 py-3">
                  <div className="text-[11px] font-medium text-ink-faint mb-1">{stat.label}</div>
                  <div className="text-sm font-semibold text-ink">{stat.value}</div>
                  <p className="text-[11px] text-ink-faint mt-1.5 leading-snug">{stat.note}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-xs font-semibold uppercase tracking-[0.08em] text-ink-faint">Branches</h2>
            {previewLoading ? (
              <p className="text-sm text-ink-faint">Loading…</p>
            ) : !preview?.branches.length ? (
              <p className="text-sm text-ink-faint">No branches listed yet.</p>
            ) : (
              <>
                <div className="sm:hidden space-y-3">
                  {preview.branches.map(b => (
                    <div key={b.id} className="rounded-xl border border-line bg-bg px-3.5 py-3 space-y-2">
                      <div>
                        <div className="font-medium text-ink">{b.name}</div>
                        {b.city ? <div className="text-xs text-ink-faint mt-0.5">{b.city}</div> : null}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-soft">
                        <span>Seats: {westernDigits(b.capacityLabel)}</span>
                        <span>Intern weeks: {formatEnNumber(b.internWeeks)}</span>
                      </div>
                      {b.sessions.length === 0 ? (
                        <p className="text-xs text-ink-faint">No sessions listed.</p>
                      ) : (
                        <ul className="space-y-1 text-xs">
                          {b.sessions.map(session => (
                            <li key={`${session.daysLabel}|${session.hoursLabel}`}>
                              <span className="text-ink">{westernDigits(session.daysLabel)}</span>
                              <span className="text-ink-faint"> · </span>
                              {westernDigits(formatHoursLabel(session.hoursLabel))}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
                <div className="hidden sm:block rounded-xl border border-line overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="bg-bg border-b border-line">
                        <th className="py-2.5 px-3.5 text-[11px] font-semibold uppercase tracking-wide text-ink-faint">Branch</th>
                        <th className="py-2.5 px-3.5 text-[11px] font-semibold uppercase tracking-wide text-ink-faint">Seats</th>
                        <th className="py-2.5 px-3.5 text-[11px] font-semibold uppercase tracking-wide text-ink-faint">Intern weeks</th>
                        <th className="py-2.5 px-3.5 text-[11px] font-semibold uppercase tracking-wide text-ink-faint">Sessions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.branches.map(b => (
                        <tr key={b.id} className="border-b border-line last:border-0 align-top">
                          <td className="py-3 px-3.5">
                            <div className="font-medium text-ink">{b.name}</div>
                            {b.city ? <div className="text-xs text-ink-faint mt-0.5">{b.city}</div> : null}
                          </td>
                          <td className="py-3 px-3.5 text-ink-soft whitespace-nowrap">{westernDigits(b.capacityLabel)}</td>
                          <td className="py-3 px-3.5 font-medium text-ink">{formatEnNumber(b.internWeeks)}</td>
                          <td className="py-3 px-3.5 text-ink-soft">
                            {b.sessions.length === 0 ? (
                              '—'
                            ) : (
                              <ul className="space-y-1 text-xs sm:text-sm">
                                {b.sessions.map(session => (
                                  <li key={`${session.daysLabel}|${session.hoursLabel}`}>
                                    <span className="text-ink">{westernDigits(session.daysLabel)}</span>
                                    <span className="text-ink-faint"> · </span>
                                    {westernDigits(formatHoursLabel(session.hoursLabel))}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>

          <section className="space-y-3 pt-1">
            <h2 className="text-xs font-semibold uppercase tracking-[0.08em] text-ink-faint">Accept</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                label="Clinic name"
                value={clinicNameTyped}
                onChange={e => setClinicNameTyped(e.target.value)}
                autoComplete="organization"
              />
              <Input
                label="Your full name"
                value={representativeName}
                onChange={e => setRepresentativeName(e.target.value)}
                autoComplete="name"
              />
              <Input
                label="Job title"
                value={representativeTitle}
                onChange={e => setRepresentativeTitle(e.target.value)}
                placeholder="e.g. Clinic owner"
              />
              <Input
                label="Date"
                type="date"
                lang="en-US"
                value={acknowledgmentDate}
                max={todayIsoDate()}
                onChange={e => setAcknowledgmentDate(e.target.value)}
              />
            </div>
            <label className="flex items-start gap-3 text-sm cursor-pointer rounded-xl border border-line bg-bg px-3.5 py-3">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={e => setConfirmed(e.target.checked)}
                className="mt-0.5 rounded border-line"
              />
              <span className="text-ink-soft leading-relaxed">
                I agree to these terms and confirm the clinic details above.
              </span>
            </label>
          </section>
        </div>

        <footer className="px-4 sm:px-8 py-3 sm:py-4 border-t border-line bg-surface shrink-0 space-y-3">
          {(error || formError) && (
            <Alert type="error">{formError || error}</Alert>
          )}
          <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={onSignOut}
              disabled={loading}
              className="sm:w-auto w-full text-sm text-ink-soft hover:text-ink py-2.5 px-3 min-h-[44px] disabled:opacity-50"
            >
              Sign out
            </button>
            <div className="flex-1" />
            {secondsLeft > 0 && (
              <p className="text-xs text-ink-faint text-center sm:text-right" aria-live="polite">
                Available in {formatEnNumber(secondsLeft)}s
              </p>
            )}
            <Button
              type="button"
              className="w-full sm:w-auto sm:min-w-[160px]"
              size="lg"
              onClick={submit}
              loading={loading}
              disabled={!canAccept}
              aria-label={secondsLeft > 0 ? `Wait ${formatEnNumber(secondsLeft)} seconds to confirm` : 'Confirm'}
            >
              {loading
                ? 'Saving…'
                : secondsLeft > 0
                  ? `Wait ${formatEnNumber(secondsLeft)}s`
                  : 'Confirm'}
            </Button>
          </div>
        </footer>
      </div>
    </ModalOverlay>
  );
}
